import React from "react";
import { Star, ExternalLink } from "lucide-react";
import {
  GOOGLE_WRITE_REVIEW_URL,
  GOOGLE_READ_REVIEWS_URL,
  REVIEWS,
} from "../lib/reviews";

/**
 * Google "G" mark rendered as inline SVG in Google's four brand colors.
 * Used only alongside links to the actual Google Business Profile — a
 * standard hospitality-industry pattern. No AggregateRating/Review JSON-LD
 * is emitted from any of these components.
 */
export function GoogleG({ size = 18, className = "" }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 48 48"
      width={size}
      height={size}
      className={className}
    >
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

export function StarRow({ count = 5, size = 14, className = "text-brass" }) {
  return (
    <div
      role="img"
      className={`inline-flex gap-0.5 ${className}`}
      aria-label={`${count} out of 5 stars`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          fill={i < count ? "currentColor" : "none"}
          strokeWidth={i < count ? 0 : 1.5}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

/**
 * Compact CTA — used in the footer and inline. Just a friendly nudge and
 * two links (Read + Write). No claims of aggregate rating.
 */
export function GoogleReviewCta({
  variant = "light", // "light" | "dark"
  compact = false,
  testId = "google-review-cta",
}) {
  const isDark = variant === "dark";
  return (
    <div
      className={`flex flex-wrap items-center gap-4 ${
        compact ? "" : "p-5 md:p-6 border"
      } ${
        isDark
          ? "border-forest-700/50 bg-forest-900/40 text-cream"
          : "border-tan bg-cream-50 text-forest-900"
      }`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <GoogleG size={26} />
        <div>
          <p
            className={`text-xs uppercase tracking-[0.16em] ${
              isDark ? "text-brass-300" : "text-brass"
            }`}
          >
            Reviews on Google
          </p>
          <p className={`text-sm md:text-base ${isDark ? "text-cream" : "text-forest-900"} font-display`}>
            Love your stay? Please leave us a Google review.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 md:ml-auto">
        <a
          href={GOOGLE_READ_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-xs uppercase tracking-[0.16em] brass-underline ${
            isDark ? "text-cream" : "text-forest-900"
          }`}
          data-testid={`${testId}-read`}
        >
          Read on Google <ExternalLink size={11} className="inline -mt-0.5 ml-1" />
        </a>
        <a
          href={GOOGLE_WRITE_REVIEW_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-brass text-cream text-xs uppercase tracking-[0.18em] hover:bg-brass-600 transition-colors"
          data-testid={`${testId}-write`}
        >
          <StarRow size={12} className="text-cream" />
          Write a review
        </a>
      </div>
    </div>
  );
}

/**
 * Full Google-reviews showcase — used on the home page.
 * Shows 4-6 5-star quotes with real reviewer first names, star row, Google
 * "G" chip, and paired Read/Write links.
 */
export function GoogleReviewShowcase({ testId = "review-showcase" }) {
  const featured = REVIEWS.slice(0, 4);
  return (
    <div className="grid md:grid-cols-2 gap-5 md:gap-8" data-testid={testId}>
      {featured.map((r) => (
        <blockquote
          key={r.id}
          className="relative bg-forest-900/40 border border-forest-700/60 p-8 md:p-10 backdrop-blur-sm"
          data-testid={`review-card-${r.id}`}
        >
          <span
            aria-hidden
            className="absolute -top-4 md:-top-6 left-6 md:left-8 font-display text-6xl md:text-7xl text-brass leading-none"
          >
            &ldquo;
          </span>
          <div className="flex items-center justify-between mb-4">
            <StarRow count={r.stars} size={16} className="text-brass" />
            <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-cream/70">
              <GoogleG size={14} />
              <span>Google review</span>
            </div>
          </div>
          <p className="font-display text-2xl md:text-3xl text-cream leading-tight mb-4 tracking-tight">
            {r.headline}
          </p>
          <p className="text-cream/80 text-[0.98rem] leading-relaxed">{r.body}</p>
          <footer className="mt-6 text-xs uppercase tracking-[0.18em] text-brass-300">
            {r.author} <span className="text-cream/50">·</span> {r.location}
          </footer>
        </blockquote>
      ))}
    </div>
  );
}

/**
 * Small strip — used on room detail + audience pages. Two quotes, plain.
 */
export function ReviewSnippetStrip({
  ids = ["royals-fan", "joe-m"],
  variant = "light",
  testId = "review-snippet-strip",
}) {
  const chosen = ids
    .map((id) => REVIEWS.find((r) => r.id === id))
    .filter(Boolean);
  if (!chosen.length) return null;
  const isDark = variant === "dark";

  return (
    <section
      className={`${isDark ? "bg-forest-900 text-cream" : "bg-cream-50"} border-y ${
        isDark ? "border-forest-700/40" : "border-tan"
      } py-12 md:py-16`}
      data-testid={testId}
    >
      <div className="container-app">
        <div className="flex items-center gap-3 mb-8">
          <GoogleG size={20} />
          <p
            className={`eyebrow ${isDark ? "text-brass-300" : "text-brass"}`}
          >
            What guests say on Google
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {chosen.map((r) => (
            <blockquote key={r.id} data-testid={`review-snippet-${r.id}`}>
              <StarRow count={5} size={14} className={isDark ? "text-brass-300" : "text-brass"} />
              <p
                className={`mt-3 font-display text-xl md:text-2xl leading-snug ${
                  isDark ? "text-cream" : "text-forest-900"
                }`}
              >
                {r.headline}
              </p>
              <p className={`mt-3 text-sm md:text-base leading-relaxed ${
                isDark ? "text-cream/75" : "text-ink/75"
              }`}>
                “{r.body}”
              </p>
              <footer
                className={`mt-4 text-xs uppercase tracking-[0.16em] ${
                  isDark ? "text-brass-300" : "text-brass"
                }`}
              >
                {r.author} · {r.location}
              </footer>
            </blockquote>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href={GOOGLE_READ_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] brass-underline ${
              isDark ? "text-cream" : "text-forest-900"
            }`}
            data-testid="review-strip-read-all"
          >
            Read all reviews on Google <ExternalLink size={12} />
          </a>
          <a
            href={GOOGLE_WRITE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-brass text-cream text-xs uppercase tracking-[0.18em] hover:bg-brass-600 transition-colors"
            data-testid="review-strip-write"
          >
            Write a review
          </a>
        </div>
      </div>
    </section>
  );
}
